import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_MODE_OPTIONS,
} from "../constants";
import type {
  CampaignInfluencerOption,
  PaymentFormData,
  PaymentStatus,
  PaymentMode,
} from "../types";

interface PaymentDetailsFieldsProps {
  formData: PaymentFormData;
  onFieldChange: <K extends keyof PaymentFormData>(
    field: K,
    value: PaymentFormData[K]
  ) => void;
  campaignInfluencers: CampaignInfluencerOption[];
}

export const PaymentDetailsFields: React.FC<PaymentDetailsFieldsProps> = ({
  formData,
  onFieldChange,
  campaignInfluencers,
}) => {
  const isPaymentDateEnabled = formData.status === "paid";

  return (
    <>
      <div className="md:col-span-2">
        <Label htmlFor="influencer">Select Influencer</Label>
        <Select
          value={formData.campaign_influencer_id}
          onValueChange={(value) =>
            onFieldChange("campaign_influencer_id", value)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose influencer" />
          </SelectTrigger>
          <SelectContent>
            {campaignInfluencers.map((ci) => (
              <SelectItem key={ci.id} value={ci.id}>
                {ci.influencers?.name} (@{ci.influencers?.handle})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="status">Payment Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) =>
            onFieldChange("status", value as PaymentStatus)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="payment_mode">Payment Mode</Label>
        <Select
          value={formData.payment_mode}
          onValueChange={(value) =>
            onFieldChange("payment_mode", value as PaymentMode)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_MODE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="due_date">Due Date</Label>
        <Input
          id="due_date"
          type="date"
          value={formData.due_date}
          onChange={(e) => onFieldChange("due_date", e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="payment_date">Payment Date</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Input
                  id="payment_date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) =>
                    onFieldChange("payment_date", e.target.value)
                  }
                  disabled={!isPaymentDateEnabled}
                  className={
                    !isPaymentDateEnabled
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : ""
                  }
                />
              </div>
            </TooltipTrigger>
            {!isPaymentDateEnabled && (
              <TooltipContent>
                <p>Payment Date can only be set when status is marked as Paid.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      <div>
        <Label htmlFor="invoice_number">Invoice Number</Label>
        <Input
          id="invoice_number"
          placeholder="INV-001"
          value={formData.invoice_number}
          onChange={(e) => onFieldChange("invoice_number", e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="transaction_id">Transaction ID</Label>
        <Input
          id="transaction_id"
          placeholder="TXN123456"
          value={formData.transaction_id}
          onChange={(e) => onFieldChange("transaction_id", e.target.value)}
        />
      </div>

      <div className="md:col-span-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes or comments"
          value={formData.notes}
          onChange={(e) => onFieldChange("notes", e.target.value)}
          rows={3}
        />
      </div>
    </>
  );
};
